import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createAirport,
  getAirportByID,
  getAllAirports,
  updateAirport,
} from "./services/airport.services";
import { clearAirportDetails } from "./airportSlice";
import { closed } from "../../components/drawer/drawerSlice";

const CreateAirport: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const AirportDetail = useAppSelector((state) => state.airport.AirportDetail);
  const AirportID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.airport.formLoading);

  useEffect(() => {
    return () => {
      dispatch(clearAirportDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (AirportID) {
      dispatch(getAirportByID(AirportID));
    }
  }, [AirportID]);

  const handleSubmit = async (values: any) => {
    try {
      if (!AirportID) {
        const insertPayload = {
          AirportName: values.AirportName,
          AirportCode: values.AirportCode,
          IsActive: values.IsActive ? values.IsActive : true,
        };
        const response = await dispatch(createAirport(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearAirportDetails());
          dispatch(getAllAirports());
        }
      } else {
        const editPayload = {
          ID: AirportID,
          AirportName: values.AirportName,
          AirportCode: values.AirportCode,
          IsActive: values.IsActive ? values.IsActive : false,
        };
        const response = await dispatch(updateAirport(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearAirportDetails());
          dispatch(getAllAirports());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={AirportDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="AirportName"
                label="Airport"
                component={FormTextField}
                validator={requiredValidator}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="AirportCode"
                label="Airport Code"
                component={FormTextField}
                validator={requiredValidator}
              />
            </GridLayoutItem>
            <GridLayoutItem
              style={{
                textAlign: "end",
                marginTop: "20px",
                width: "100%",
              }}
            >
              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginRight: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
              <ButtonWithLoading
                label={AirportID ? "Update" : "Create"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateAirport;
